import React, { useMemo } from 'react';
import {
  ObjectiveCategoryFragment,
  ObjectiveTimePeriodFragment,
  useGoalsContextPeriodsQuery,
} from '../../../../__generated__/graphql';
import { useFlairLocalStorage } from '../../../../hooks/useFlairLocalStorage';
import ServerError from '../../../../../../components/ServerError';

export type GoalsContextType = {
  isManagerView: boolean;
  allActivePeriods: readonly ObjectiveTimePeriodFragment[];
  currentPeriod: ObjectiveTimePeriodFragment | null;
  currentPeriodId: string | null;
  setCurrentPeriodId: (periodId: string | null) => void;
  loading: boolean;
  developmentFrameworkEnabled: boolean;
  currentPeriodGoalCategories: readonly ObjectiveCategoryFragment[];
};

const GoalsContext = React.createContext<GoalsContextType>({
  isManagerView: false,
  allActivePeriods: [],
  currentPeriod: null,
  currentPeriodId: null,
  setCurrentPeriodId: () => {},
  loading: true,
  developmentFrameworkEnabled: false,
  currentPeriodGoalCategories: [],
});

type Props = {
  isManagerView: boolean;
};

export const GoalsContextProvider: React.FC<Props> = (props) => {
  const [currentPeriodIdFromStorage, setCurrentPeriodIdFromStorage] =
    useFlairLocalStorage('goals_current_period_id', null);
  const { data, loading, error } = useGoalsContextPeriodsQuery();

  const allActivePeriods = useMemo(
    () => data?.activeObjectivePeriods ?? [],
    [data?.activeObjectivePeriods],
  );

  const currentPeriod = useMemo(() => {
    if (!currentPeriodIdFromStorage) {
      return null;
    }
    return (
      allActivePeriods.find(
        (period) => period.Id === currentPeriodIdFromStorage,
      ) ?? null
    );
  }, [currentPeriodIdFromStorage, allActivePeriods]);

  const currentPeriodId = useMemo(() => {
    return currentPeriod?.Id ?? null;
  }, [currentPeriod]);

  const developmentFrameworkEnabled = useMemo(
    () => allActivePeriods.length > 0,
    [allActivePeriods.length],
  );

  const currentPeriodGoalCategories = useMemo(
    () => currentPeriod?.framework?.categories ?? [],
    [currentPeriod?.framework?.categories],
  );

  // we don't need to useEffect for this (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  if (currentPeriodIdFromStorage === null && allActivePeriods.length === 1) {
    setCurrentPeriodIdFromStorage(allActivePeriods[0].Id);
  }
  if (error) {
    return <ServerError />;
  }

  return (
    <GoalsContext.Provider
      value={{
        isManagerView: props.isManagerView,
        allActivePeriods,
        currentPeriod,
        currentPeriodId,
        setCurrentPeriodId: setCurrentPeriodIdFromStorage,
        loading,
        developmentFrameworkEnabled,
        currentPeriodGoalCategories,
      }}
      {...props}
    />
  );
};

export const useGoalsContext = (): GoalsContextType =>
  React.useContext(GoalsContext);
